import { type ReactNode, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  hover?: boolean;
  glow?: boolean;
  padded?: boolean;
}

export function Card({ children, className, hover, glow, padded = true, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'relative rounded-2xl bg-surface border-2 border-fg/85 dark:border-fg/30 shadow-sticker-sm',
        'transition-all duration-150',
        hover && 'hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-sticker active:translate-x-[1px] active:translate-y-[1px] active:shadow-sticker-pressed cursor-pointer',
        glow && 'hover:shadow-glow',
        padded && 'p-5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mb-4', className)}>{children}</div>;
}

export function CardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h3 className={cn('text-sm font-semibold text-fg', className)}>{children}</h3>;
}

export function CardDescription({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-xs text-fg-muted mt-0.5', className)}>{children}</p>;
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('', className)}>{children}</div>;
}

export function CardFooter({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mt-4 pt-4 border-t border-border flex items-center justify-end gap-2', className)}>{children}</div>;
}
